import { factory, getResponseCamelization } from '@repo-level/core-front';
import type {
  CMSApiBase,
  CMSGroupParams,
  CMSImageBase,
  ICMSFAQItem,
} from 'api/cms/base';
import { buildCmsUrl, getResponseCMS } from 'api/cms/base';

export interface ICMSCurrencyAmount {
  id: number;
  currency: string;
  amount: number;
}

export type ICMSLotteryImage = Pick<CMSImageBase, 'id' | 'url'>;

export interface ICMSLotteryHero {
  id: number;
  title: string;
  description: string | null;
  imageDesktop: ICMSLotteryImage;
  imageMobile: ICMSLotteryImage;
  backgroundDesktop: ICMSLotteryImage;
  backgroundMobile: ICMSLotteryImage;
}

/** no CMS description: it depends on the player, so it comes from translations */
export type ICMSLotteryHeroEnded = Omit<ICMSLotteryHero, 'description'>;

/** the `__component` of a dynamic zone entry, camelized into `_Component` */
export enum ECMSLotteryPrize {
  CASH = 'lottery.prize-cash',
  FREE_SPINS = 'lottery.prize-free-spins',
  PHYSICAL = 'lottery.prize-physical',
}

interface ICMSLotteryPrizeBase {
  id: number;
  /** the SS prize name, exactly as in the back office */
  stuff: string;
  /** shown instead of `stuff` when set */
  name: string | null;
  count: number;
  image: ICMSLotteryImage;
  wager: number;
}

export interface ICMSLotteryCashPrize extends ICMSLotteryPrizeBase {
  _Component: ECMSLotteryPrize.CASH;
  amounts: ICMSCurrencyAmount[];
}

export interface ICMSLotteryFreeSpinsPrize extends ICMSLotteryPrizeBase {
  _Component: ECMSLotteryPrize.FREE_SPINS;
  fsCount: number;
  /** per spin */
  bet: ICMSCurrencyAmount[];
  fsGame: string | null;
  fsMaxWin: number | null;
}

export interface ICMSLotteryPhysicalPrize extends ICMSLotteryPrizeBase {
  _Component: ECMSLotteryPrize.PHYSICAL;
  /** the cash equivalent */
  amounts: ICMSCurrencyAmount[];
}

export type ICMSLotteryPrize =
  | ICMSLotteryCashPrize
  | ICMSLotteryFreeSpinsPrize
  | ICMSLotteryPhysicalPrize;

export type ICMSLotteryVisibility = Pick<
  CMSGroupParams,
  'groupsIncludeMode' | 'groupsInclude' | 'groupsExcludeMode' | 'groupsExclude'
>;

/** numbered by its position */
export interface ICMSLotteryStep {
  id: number;
  title: string;
  description: string;
}

export interface ICMSLottery extends CMSApiBase {
  name: string;
  ssLotteryId: number;
  /** from then on the lottery is on the page, its dates come from SS */
  showFrom: string;
  visibility: ICMSLotteryVisibility | null;
  heroComingSoon: ICMSLotteryHero;
  heroStarted: ICMSLotteryHero;
  heroEnded: ICMSLotteryHeroEnded;
  prizePool: ICMSCurrencyAmount[];
  prizes: ICMSLotteryPrize[];
  popupBackground: ICMSLotteryImage;
  /** the deposit form falls back to a bundled image */
  depositFormImage: ICMSLotteryImage | null;
  howItWorks: ICMSLotteryStep[];
  faq: ICMSFAQItem[];
}

// a lottery is monthly: the window holds the previous one, the live one and the drawn one a
// winner may still have to see in the popup
const SHOWN_DAYS = 90;

const URL_ONLY = { fields: ['url'] };

const HERO = {
  populate: {
    image_desktop: URL_ONLY,
    image_mobile: URL_ONLY,
    background_desktop: URL_ONLY,
    background_mobile: URL_ONLY,
  },
};

const POPULATE = {
  visibility: true,
  hero_coming_soon: HERO,
  hero_started: HERO,
  hero_ended: HERO,
  prize_pool: true,
  prizes: {
    on: {
      [ECMSLotteryPrize.CASH]: { populate: { image: URL_ONLY, amounts: true } },
      [ECMSLotteryPrize.FREE_SPINS]: {
        populate: { image: URL_ONLY, bet: true },
      },
      [ECMSLotteryPrize.PHYSICAL]: {
        populate: { image: URL_ONLY, amounts: true },
      },
    },
  },
  popup_background: URL_ONLY,
  deposit_form_image: URL_ONLY,
  how_it_works: true,
  faq: true,
};

// floored to the day, so the URL stays the same and the CMS cache works
const getShownFrom = () => {
  const date = new Date();

  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - SHOWN_DAYS);

  return date.toISOString();
};

/** future entries come too: the next one replaces the current one on time, without a reload */
export const cmsLotteries = factory(
  (r) => (lang: string) =>
    r<ICMSLottery[]>(
      'get',
      buildCmsUrl({
        path: 'lotteries',
        lang,
        additionalParams: { sort: 'show_from:asc' },
      }),
      // nested, the request serializes it with qs
      {
        filters: { show_from: { $gte: getShownFrom() } },
        populate: POPULATE,
      },
      { transformResponse: [getResponseCamelization(), getResponseCMS] }
    )
);
